import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiAlertTriangle,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiInfo,
  FiRefreshCw,
  FiSliders,
  FiX,
} from 'react-icons/fi';
import apiClient from '../../Config/apiClient';
import { API_ENDPOINTS } from '../../Config/api';
import DepartmentHeatmap from './DepartmentHeatmap';
import DepartmentDrilldown from './DepartmentDrilldown';
import {
  DASH,
  fmtDelta,
  fmtIndex,
  fmtPercent,
  isOutOfTarget,
  levelStyle,
  maxRiskWeight,
  normalizeHeatmap,
  sortByAwareness,
  sortedLevels,
  suggestTarget,
} from '../../Utils/awarenessDepartments';

/**
 * Vista di dettaglio dell'awareness per reparto.
 *
 * I numeri arrivano già aggregati dalla piattaforma Awareness (indice, livello,
 * pesi, soglie): qui non si calcola nulla e non si sommano gli utenti con totali
 * presi altrove, perché il payload conta i soli utenti attivi.
 * L'unica configurazione lato A-Sight è il target atteso per reparto.
 *
 * La heatmap è caricata a richiesta (`?heatmap=true`): è la parte pesante del
 * payload e serve solo a chi apre la matrice o il dettaglio di un reparto.
 */
export default function AwarenessByDepartment({ companyId = null }) {
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Gli errori sono tenuti come stato "grezzo" e tradotti al momento del render:
  // così `t` non entra nelle dipendenze dei callback e non può innescare rifetch.
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);

  const [heatmap, setHeatmap] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  // Errore isolato: se la heatmap non arriva, la tabella dei reparti resta visibile.
  const [heatmapFailed, setHeatmapFailed] = useState(false);

  const [editingTargets, setEditingTargets] = useState(false);
  const [targetDraft, setTargetDraft] = useState({});
  const [savingTargets, setSavingTargets] = useState(false);
  const [targetError, setTargetError] = useState('');

  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  const baseUrl = companyId
    ? API_ENDPOINTS.awarenessDepartmentsCompany(companyId)
    : API_ENDPOINTS.awarenessDepartments;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Riepilogo: nessuna heatmap, si carica solo quando serve davvero.
      const response = await apiClient.get(baseUrl);
      setData(response.data);
      setHeatmap(null);
    } catch (err) {
      console.error('Error fetching awareness by department:', err);
      setError({ message: err.response?.data?.message || null });
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Scarica la heatmap una sola volta, alla prima richiesta dell'utente. */
  const ensureHeatmap = useCallback(async () => {
    if (heatmap || heatmapLoading) return;
    setHeatmapLoading(true);
    setHeatmapFailed(false);
    try {
      const response = await apiClient.get(baseUrl, { params: { heatmap: 'true' } });
      setHeatmap(normalizeHeatmap(response.data?.heatmap));
    } catch (err) {
      console.error('Error fetching awareness heatmap:', err);
      setHeatmapFailed(true);
    } finally {
      setHeatmapLoading(false);
    }
  }, [baseUrl, heatmap, heatmapLoading]);

  const levels = data?.levels || {};
  const weights = data?.index_weights || {};
  const smallSampleUsers = data?.small_sample_users;
  const targets = useMemo(() => data?.targets || {}, [data]);
  const hasTargets = Object.keys(targets).length > 0;

  const departments = useMemo(() => sortByAwareness(data?.departments || []), [data]);
  const companyAverage = data?.company_average || null;
  const unassigned = data?.unassigned || null;
  const maxRisk = useMemo(() => maxRiskWeight(data?.departments || []), [data]);
  const weakest = departments[0] || null;

  // Il peso di rischio è informativo solo se distingue i reparti fra loro.
  const riskWeightIsRelevant = useMemo(() => {
    const distinct = new Set((data?.departments || []).map((d) => d.risk_weight));
    return distinct.size > 1;
  }, [data]);

  const anySmallSample = useMemo(
    () => (data?.departments || []).some((d) => d.small_sample) || Boolean(unassigned?.small_sample),
    [data, unassigned]
  );

  const outOfTargetCount = useMemo(
    () => departments.filter((d) => isOutOfTarget(d, targets[String(d.department_id)])).length,
    [departments, targets]
  );

  const heatmapByDepartment = useMemo(() => {
    const map = new Map();
    (heatmap?.departments || []).forEach((d) => map.set(d.department_id, d));
    return map;
  }, [heatmap]);

  const selectedDepartment = useMemo(
    () => departments.find((d) => d.department_id === selectedDepartmentId) || null,
    [departments, selectedDepartmentId]
  );

  const openDepartment = (departmentId) => {
    setSelectedDepartmentId(departmentId);
    ensureHeatmap();
  };

  const toggleHeatmap = () => {
    const next = !heatmapOpen;
    setHeatmapOpen(next);
    if (next) ensureHeatmap();
  };

  const openTargetEditor = () => {
    const draft = {};
    departments.forEach((dept) => {
      const key = String(dept.department_id);
      const current = targets[key];
      draft[key] = current === null || current === undefined ? '' : String(current);
    });
    setTargetDraft(draft);
    setTargetError('');
    setEditingTargets(true);
  };

  const applySuggestions = () => {
    const draft = { ...targetDraft };
    departments.forEach((dept) => {
      const suggested = suggestTarget(dept.risk_weight, maxRisk, levels);
      if (suggested !== null && suggested !== undefined) {
        draft[String(dept.department_id)] = String(suggested);
      }
    });
    setTargetDraft(draft);
  };

  const saveTargets = async () => {
    setSavingTargets(true);
    setTargetError('');
    try {
      const payload = departments.map((dept) => {
        const key = String(dept.department_id);
        const raw = targetDraft[key];
        return {
          department_id: dept.department_id,
          department_name: dept.department_name || '',
          target_index: raw === '' || raw === undefined ? null : Number(raw),
        };
      });
      const invalid = payload.find(
        (entry) =>
          entry.target_index !== null &&
          (!Number.isFinite(entry.target_index) || entry.target_index < 0 || entry.target_index > 100)
      );
      if (invalid) {
        setTargetError(t('awarenessDepartments.targetRangeError'));
        setSavingTargets(false);
        return;
      }

      const url = companyId
        ? API_ENDPOINTS.awarenessDepartmentTargetsCompany(companyId)
        : API_ENDPOINTS.awarenessDepartmentTargets;
      const response = await apiClient.put(url, { targets: payload });
      setData((prev) => (prev ? { ...prev, targets: response.data?.targets || {} } : prev));
      setEditingTargets(false);
    } catch (err) {
      console.error('Error saving awareness targets:', err);
      setTargetError(err.response?.data?.message || t('awarenessDepartments.targetSaveError'));
    } finally {
      setSavingTargets(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setExportFailed(false);
    try {
      const url = companyId
        ? API_ENDPOINTS.awarenessDepartmentsExportCompany(companyId)
        : API_ENDPOINTS.awarenessDepartmentsExport;
      const response = await apiClient.get(url, {
        params: { heatmap: 'true' },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', 'awareness_per_reparto.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error exporting awareness by department:', err);
      setExportFailed(true);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6 w-full flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4 w-full max-w-md">
          <div className="h-4 bg-gray-200 rounded w-40" />
          <div className="h-3 bg-gray-200 rounded w-full" />
          <div className="h-3 bg-gray-200 rounded w-full" />
          <div className="h-3 bg-gray-200 rounded w-2/3" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white border rounded-lg p-6 w-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <FiAlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>
            {error
              ? error.message || t('awarenessDepartments.loadingError')
              : t('awarenessDepartments.noData')}
          </p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto"
          >
            <FiRefreshCw className="w-4 h-4" /> {t('awarenessDashboard.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!departments.length && !unassigned) {
    return (
      <div className="bg-white border rounded-lg p-6 w-full text-center text-gray-500">
        <FiInfo className="w-10 h-10 mx-auto mb-2 text-gray-400" />
        <p>{t('awarenessDepartments.noDepartments')}</p>
        <p className="text-sm text-gray-400 mt-1">{t('awarenessDepartments.noDepartmentsHint')}</p>
      </div>
    );
  }

  /** "completamento 50%, quiz 30%, phishing 20%" — costruita dai pesi del payload. */
  const weightsSentence = Object.entries(weights)
    .map(([key, value]) => `${t(`awarenessDepartments.weights.${key}`, key).toLowerCase()} ${Math.round(Number(value) * 100)}%`)
    .join(', ');

  const componentColumns = [
    { key: 'completion', label: t('awarenessDepartments.completion'), value: (e) => fmtPercent(e.completion_rate) },
    { key: 'quiz', label: t('awarenessDepartments.quizScore'), value: (e) => fmtPercent(e.average_quiz_score) },
    {
      key: 'phishing',
      label: t('awarenessDepartments.phishingClickRate'),
      value: (e) => fmtPercent(e.phishing_click_rate),
      hint: t('awarenessDepartments.lowerIsBetter'),
    },
  ];

  const renderRow = (entry, { label, isAverage = false, outsideRanking = false } = {}) => {
    const key = String(entry.department_id);
    const style = levelStyle(entry.level, levels);
    // Il target vale per i reparti in classifica: non per la media né per chi è senza reparto.
    const target = isAverage || outsideRanking ? undefined : targets[key];
    const offTarget = isOutOfTarget(entry, target);

    return (
      <tr
        key={label ? `row-${label}` : `dept-${key}`}
        className={
          isAverage
            ? 'bg-yellow-50/70 border-b-2 border-yellow-200'
            : `hover:bg-gray-50 ${offTarget ? 'bg-red-50/50' : ''}`
        }
      >
        {/* Reparto */}
        <td className="px-3 sm:px-4 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            {isAverage || outsideRanking ? (
              <span className={isAverage ? 'font-semibold text-gray-900' : 'text-gray-700'}>
                {label || entry.department_name || DASH}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => openDepartment(entry.department_id)}
                className="font-medium text-gray-900 hover:text-blue-700 hover:underline text-left"
              >
                {entry.department_name || DASH}
              </button>
            )}
            {entry.small_sample && (
              <span
                className="text-gray-400 text-xs"
                title={t('awarenessDepartments.smallSampleHint', { count: smallSampleUsers })}
              >
                *
              </span>
            )}
            {outsideRanking && (
              <span className="text-[10px] text-gray-500 border border-gray-300 rounded px-1 py-0.5">
                {t('awarenessDepartments.outsideRanking')}
              </span>
            )}
          </div>
          {!isAverage && !outsideRanking && riskWeightIsRelevant && (
            <div className="text-[11px] text-gray-400 mt-0.5">
              {t('awarenessDepartments.riskWeight')}: {entry.risk_weight ?? DASH}
            </div>
          )}
        </td>

        {/* Utenti attivi */}
        <td className="px-2 py-3 text-center text-gray-600 tabular-nums">{entry.users_count ?? DASH}</td>

        {/* Indice + livello */}
        <td className="px-3 sm:px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden hidden lg:block">
              <div
                className={`h-1.5 rounded-full ${style.bar}`}
                style={{
                  width:
                    entry.awareness_index === null || entry.awareness_index === undefined
                      ? '0%'
                      : `${Math.min(100, Math.max(0, Number(entry.awareness_index)))}%`,
                }}
              />
            </div>
            <span className="font-bold text-gray-900 tabular-nums w-10 text-right">
              {fmtIndex(entry.awareness_index)}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${style.badge}`}>
              {t(`awarenessDepartments.levels.${entry.level}`, entry.level || DASH)}
            </span>
          </div>
        </td>

        {/* Scarto vs media */}
        <td className="px-2 py-3 text-center tabular-nums">
          {entry.vs_company_average === null || entry.vs_company_average === undefined ? (
            <span className="text-gray-300">{DASH}</span>
          ) : (
            <span className={Number(entry.vs_company_average) < 0 ? 'text-red-600 font-medium' : 'text-emerald-600 font-medium'}>
              {fmtDelta(entry.vs_company_average)}
            </span>
          )}
        </td>

        {/* Target (colonna presente solo se almeno un target è configurato) */}
        {hasTargets && (
          <td className="px-2 py-3 text-center tabular-nums">
            {target === null || target === undefined ? (
              <span className="text-gray-300">{DASH}</span>
            ) : offTarget ? (
              <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                <FiAlertTriangle className="w-3.5 h-3.5" /> {target}
              </span>
            ) : (
              <span className="text-emerald-700 font-semibold">{target}</span>
            )}
          </td>
        )}

        {/* Componenti dell'indice, visivamente secondarie */}
        {componentColumns.map((col) => (
          <td key={col.key} className="px-2 py-3 text-center text-sm text-gray-500 tabular-nums bg-gray-50/50">
            {col.value(entry)}
          </td>
        ))}
      </tr>
    );
  };

  const averageStyle = companyAverage ? levelStyle(companyAverage.level, levels) : null;
  const weakestStyle = weakest ? levelStyle(weakest.level, levels) : null;

  return (
    <div className="space-y-4 w-full">
      {/* Sintesi: la risposta a colpo d'occhio, prima dei numeri di dettaglio */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
            {companyAverage && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50/70 px-3 py-2">
                <div className="text-[11px] text-gray-600 uppercase tracking-wide">
                  {t('awarenessDepartments.companyAverage')}
                </div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-2xl font-bold text-gray-900">{fmtIndex(companyAverage.awareness_index)}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${averageStyle.badge}`}>
                    {t(`awarenessDepartments.levels.${companyAverage.level}`, companyAverage.level || DASH)}
                  </span>
                </div>
              </div>
            )}

            {weakest && (
              <div className="rounded-lg border border-gray-200 px-3 py-2">
                <div className="text-[11px] text-gray-600 uppercase tracking-wide">
                  {t('awarenessDepartments.weakestDepartment')}
                </div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-2xl font-bold text-gray-900">{fmtIndex(weakest.awareness_index)}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${weakestStyle.badge}`}>
                    {t(`awarenessDepartments.levels.${weakest.level}`, weakest.level || DASH)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 truncate">{weakest.department_name || DASH}</div>
              </div>
            )}

            <div
              className={`rounded-lg border px-3 py-2 ${
                outOfTargetCount > 0 ? 'border-red-200 bg-red-50/60' : 'border-gray-200'
              }`}
            >
              <div className="text-[11px] text-gray-600 uppercase tracking-wide">
                {t('awarenessDepartments.target')}
              </div>
              {!hasTargets ? (
                <>
                  <div className="text-sm text-gray-600 mt-1">{t('awarenessDepartments.targetsNotConfigured')}</div>
                  <button
                    type="button"
                    onClick={openTargetEditor}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                  >
                    {t('awarenessDepartments.configureNow')}
                  </button>
                </>
              ) : outOfTargetCount > 0 ? (
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-2xl font-bold text-red-700">{outOfTargetCount}</span>
                  <span className="text-xs text-red-700">{t('awarenessDepartments.outOfTargetShort')}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-sm text-emerald-700 font-medium mt-1">
                  <FiCheck className="w-4 h-4" /> {t('awarenessDepartments.allOnTarget')}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={openTargetEditor}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-lg"
            >
              <FiSliders className="w-4 h-4" /> {t('awarenessDepartments.configureTargets')}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-3 py-2 bg-yellow-400 hover:bg-yellow-500 text-black text-sm font-semibold rounded-lg disabled:opacity-60"
            >
              <FiDownload className="w-4 h-4" />
              {exporting ? t('awarenessDepartments.exporting') : t('awarenessDepartments.export')}
            </button>
            <button
              type="button"
              onClick={fetchData}
              title={t('awarenessDepartments.refresh')}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-lg"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {exportFailed && (
          <p className="mt-3 text-xs text-red-700">{t('awarenessDepartments.exportError')}</p>
        )}

        {/* Una riga per spiegare che cos'è l'indice, con i pesi presi dal payload */}
        {weightsSentence && (
          <p className="mt-3 text-xs text-gray-500 flex items-start gap-1.5">
            <FiInfo className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            {t('awarenessDepartments.indexExplained', { weights: weightsSentence })}
          </p>
        )}
      </div>

      {/* Tabella reparti */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 pt-3 pb-2">
          <h3 className="font-semibold text-gray-900">{t('awarenessDepartments.tableTitle')}</h3>
          <p className="text-xs text-gray-500">{t('awarenessDepartments.tableHint')}</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-400">
                <th className="px-3 sm:px-4 pt-2 text-left" colSpan={hasTargets ? 5 : 4} />
                <th
                  className="px-2 pt-2 pb-1 text-center font-semibold bg-gray-50/50 border-b border-gray-200"
                  colSpan={componentColumns.length}
                >
                  {t('awarenessDepartments.indexComponents')}
                </th>
              </tr>
              <tr className="bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <th className="px-3 sm:px-4 py-2.5 text-left">{t('awarenessDepartments.department')}</th>
                <th className="px-2 py-2.5 text-center">{t('awarenessDepartments.activeUsersShort')}</th>
                <th className="px-3 sm:px-4 py-2.5 text-left">{t('awarenessDepartments.awarenessIndex')}</th>
                <th className="px-2 py-2.5 text-center">{t('awarenessDepartments.vsAverage')}</th>
                {hasTargets && <th className="px-2 py-2.5 text-center">{t('awarenessDepartments.target')}</th>}
                {componentColumns.map((col) => {
                  const weight = weights[col.key];
                  return (
                    <th
                      key={col.key}
                      className="px-2 py-2.5 text-center font-medium normal-case text-gray-500 bg-gray-50/50"
                      title={col.hint}
                    >
                      <div>{col.label}</div>
                      {weight !== undefined && (
                        <div className="text-[10px] font-normal text-gray-400">
                          {Math.round(Number(weight) * 100)}%
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {companyAverage &&
                renderRow(companyAverage, { label: t('awarenessDepartments.companyAverage'), isAverage: true })}
              {departments.map((dept) => renderRow(dept))}
              {unassigned &&
                renderRow(unassigned, { label: t('awarenessDepartments.unassigned'), outsideRanking: true })}
            </tbody>
          </table>
        </div>

        {/* Legenda essenziale: livelli, nota sui campioni ridotti, data del dato */}
        <div className="px-4 py-2.5 border-t bg-gray-50 text-[11px] text-gray-500 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-2">
            {sortedLevels(levels).map((level) => {
              const style = levelStyle(level.key, levels);
              return (
                <span key={level.key} className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                  {t(`awarenessDepartments.levels.${level.key}`, level.key)} ≥ {level.threshold}
                </span>
              );
            })}
          </span>
          {anySmallSample && smallSampleUsers ? (
            <span>{t('awarenessDepartments.smallSampleFootnote', { count: smallSampleUsers })}</span>
          ) : null}
          {data.counts_active_users_only && <span>{t('awarenessDepartments.activeOnlyShort')}</span>}
          {data.generated_at && (
            <span className="ml-auto">
              {t('awarenessDepartments.generatedAt')} {new Date(data.generated_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Heatmap reparti x moduli: chiusa di default, caricata solo quando si apre */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={toggleHeatmap}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
        >
          <div>
            <h3 className="font-semibold text-gray-900">{t('awarenessDepartments.heatmapTitle')}</h3>
            <p className="text-xs text-gray-500">{t('awarenessDepartments.heatmapSubtitle')}</p>
          </div>
          <span className="flex items-center gap-2 text-sm font-semibold text-blue-600 shrink-0">
            {heatmapOpen ? t('awarenessDepartments.hide') : t('awarenessDepartments.show')}
            {heatmapOpen ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
          </span>
        </button>

        {heatmapOpen && (
          <div className="border-t border-gray-200">
            {heatmapLoading ? (
              <div className="p-6 text-center text-sm text-gray-500">
                {t('awarenessDepartments.loadingHeatmap')}
              </div>
            ) : heatmapFailed ? (
              <div className="p-6 text-center text-sm text-gray-500">
                <p>{t('awarenessDepartments.heatmapError')}</p>
                <button
                  onClick={ensureHeatmap}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto"
                >
                  <FiRefreshCw className="w-4 h-4" /> {t('awarenessDashboard.retry')}
                </button>
              </div>
            ) : (
              <DepartmentHeatmap
                heatmap={heatmap}
                smallSampleUsers={smallSampleUsers}
                onSelectDepartment={openDepartment}
              />
            )}
          </div>
        )}
      </div>

      {/* Drill-down reparto -> moduli del percorso */}
      {selectedDepartment && (
        <DepartmentDrilldown
          department={selectedDepartment}
          heatmapDepartment={heatmapByDepartment.get(selectedDepartment.department_id)}
          loadingModules={heatmapLoading}
          levels={levels}
          smallSampleUsers={smallSampleUsers}
          target={targets[String(selectedDepartment.department_id)]}
          onClose={() => setSelectedDepartmentId(null)}
        />
      )}

      {/* Configurazione dei target attesi */}
      {editingTargets && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('awarenessDepartments.configureTargetsTitle')}
                  </h3>
                  <p className="text-sm text-gray-500">{t('awarenessDepartments.configureTargetsHint')}</p>
                </div>
                <button
                  onClick={() => setEditingTargets(false)}
                  className="text-gray-400 hover:text-gray-600 shrink-0"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              {targetError && (
                <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm">
                  {targetError}
                </div>
              )}

              <button
                type="button"
                onClick={applySuggestions}
                className="mb-4 text-sm font-semibold text-blue-600 hover:text-blue-800"
              >
                {t('awarenessDepartments.applySuggestions')}
              </button>

              <div className="space-y-3">
                {departments.map((dept) => {
                  const key = String(dept.department_id);
                  const suggested = suggestTarget(dept.risk_weight, maxRisk, levels);
                  return (
                    <div key={key} className="flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[160px]">
                        <div className="text-sm font-medium text-gray-800">
                          {dept.department_name || DASH}
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {t('awarenessDepartments.riskWeight')}: {dept.risk_weight ?? DASH} ·{' '}
                          {t('awarenessDepartments.currentIndex')}: {fmtIndex(dept.awareness_index)}
                          {suggested !== null && suggested !== undefined && (
                            <> · {t('awarenessDepartments.suggested')}: {suggested}</>
                          )}
                        </div>
                      </div>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        placeholder={t('awarenessDepartments.noTarget')}
                        className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        value={targetDraft[key] ?? ''}
                        onChange={(e) =>
                          setTargetDraft((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                      />
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingTargets(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium"
                >
                  {t('awarenessPage.cancel')}
                </button>
                <button
                  type="button"
                  onClick={saveTargets}
                  disabled={savingTargets}
                  className="flex items-center gap-2 px-5 py-2 bg-yellow-400 hover:bg-yellow-500 text-black rounded-lg font-semibold disabled:opacity-60"
                >
                  <FiCheck className="w-4 h-4" />
                  {savingTargets ? t('awarenessDepartments.saving') : t('awarenessPage.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
